import { z } from 'zod';
import { isoDate, reason, uuid } from './schemas.js';
import type { PeriodDto } from './periods.js';

const currency = z
  .string()
  .trim()
  .toUpperCase()
  .regex(/^[A-Z]{3}$/, 'Use a 3-letter currency code, e.g. EGP');
const money = z.number().finite().min(0).max(1_000_000_000);

export const compensationSchema = z
  .object({
    effectiveFrom: isoDate,
    salaryAmount: money.refine((n) => n > 0, 'Enter the monthly salary'),
    salaryCurrency: currency,
    payCurrency: currency,
    /** Fixed conversion rate (pay currency per 1 salary currency). null = use each month's rate. */
    fixedRate: z.number().positive().max(1_000_000).nullable().default(null),
    overtimeEligible: z.boolean().default(true),
    note: z.string().trim().max(500).optional(),
  })
  .strict();
export type CompensationInput = z.infer<typeof compensationSchema>;

/** IBAN check digits (ISO 13616, mod 97 = 1). Input already upper-case without spaces. */
export function ibanValid(iban: string): boolean {
  if (!/^[A-Z]{2}[0-9]{2}[A-Z0-9]{10,30}$/.test(iban)) return false;
  // Known country lengths for the region PCBS works in; others are only checked by mod 97.
  const LENGTHS: Record<string, number> = { EG: 29, SA: 24, LB: 28, AE: 23, JO: 30, KW: 30, QA: 29, BH: 22, GB: 22, DE: 22, FR: 27 };
  const want = LENGTHS[iban.slice(0, 2)];
  if (want && iban.length !== want) return false;
  const moved = iban.slice(4) + iban.slice(0, 4);
  let rem = 0;
  for (const ch of moved) {
    const v = ch >= 'A' ? String(ch.charCodeAt(0) - 55) : ch;
    for (const dgt of v) rem = (rem * 10 + Number(dgt)) % 97;
  }
  return rem === 1;
}

/** Spaces and dashes removed, upper case (how people copy numbers from bank papers). */
const compact = (s: string) => s.replace(/[\s]+/g, '').toUpperCase();
const optionalText = <T extends z.ZodTypeAny>(schema: T) => z.preprocess((v) => (typeof v === 'string' && !v.trim() ? null : v), schema.nullable()).default(null);

export const bankAccountSchema = z
  .object({
    accountHolder: z.string().trim().min(2, 'Write the name exactly as it appears on the bank account').max(140),
    bankName: z.string().trim().min(2, 'Enter the bank name').max(140),
    accountNumber: optionalText(
      z
        .string()
        .transform(compact)
        .pipe(z.string().regex(/^[0-9A-Z-]{4,34}$/, 'Use only digits, letters and dashes (4–34 characters)')),
    ),
    iban: optionalText(
      z
        .string()
        .transform((s) => compact(s).replace(/-/g, ''))
        .pipe(z.string().refine(ibanValid, 'This IBAN is not valid — check it against the bank letter (an Egyptian IBAN is EG + 27 digits)')),
    ),
    swift: optionalText(
      z
        .string()
        .transform(compact)
        .pipe(z.string().regex(/^[A-Z]{6}[A-Z0-9]{2}([A-Z0-9]{3})?$/, 'A SWIFT / BIC code has 8 or 11 letters and digits')),
    ),
  })
  .strict()
  .refine((b) => b.accountNumber || b.iban, { message: 'Enter the account number or the IBAN', path: ['accountNumber'] });
export type BankAccountInput = z.infer<typeof bankAccountSchema>;

export interface BankAccountDto {
  accountHolder: string;
  bankName: string;
  accountNumber: string | null;
  iban: string | null;
  swift: string | null;
  updatedBy: string | null;
  updatedAt: string;
}

/** "•••• 1234" — what payslips and the audit trail show. */
export function maskAccount(v: string | null | undefined): string | null {
  if (!v) return null;
  return v.length <= 4 ? v : `•••• ${v.slice(-4)}`;
}

export const payrollRateSchema = z
  .object({ fromCurrency: currency, toCurrency: currency, rate: z.number().positive().max(1_000_000) })
  .strict();

export const payrollAdjustmentSchema = z
  .object({
    employeeId: uuid,
    amount: z.number().finite().refine((n) => n !== 0, 'Enter a non-zero amount').refine((n) => Math.abs(n) <= 1_000_000_000, 'Too large'),
    note: z.string().trim().min(3, 'Say what it is for').max(300),
  })
  .strict();

export const reopenPayrollSchema = z.object({ reason }).strict();

export interface CompensationDto {
  id: string;
  effectiveFrom: string;
  salaryAmount: number;
  salaryCurrency: string;
  payCurrency: string;
  fixedRate: number | null;
  overtimeEligible: boolean;
  note: string | null;
  createdBy: string | null;
  createdAt: string;
  isCurrent: boolean;
  isFuture: boolean;
}

export interface PayrollRateDto {
  fromCurrency: string;
  toCurrency: string;
  rate: number;
  source: 'manual' | 'online';
  sourceNote: string | null;
  updatedAt: string;
  updatedBy: string | null;
}

export interface PayrollAdjustmentDto {
  id: string;
  amount: number;
  note: string;
  createdBy: string | null;
  createdAt: string;
}

export interface PayrollLineDto {
  employeeId: string;
  employeeName: string;
  employeeCode: string;
  /** Salary record used (the one in effect on the last day of the period). */
  salaryAmount: number;
  salaryCurrency: string;
  payCurrency: string;
  rate: number | null;
  rateSource: 'same_currency' | 'fixed' | 'period' | 'missing';
  /** Share of the period the person was employed (1 = whole period). */
  employedShare: number;
  timeTracked: boolean;
  overtimeEligible: boolean;
  baseSalary: number;
  requiredHours: number;
  workedHours: number;
  paidLeaveHours: number;
  paidHours: number;
  overtimeHours: number;
  shortHours: number;
  hourlyRate: number;
  overtimeMultiplier: number;
  overtimePay: number;
  deduction: number;
  adjustments: PayrollAdjustmentDto[];
  adjustmentsTotal: number;
  netPay: number;
  notes: string[];
}

export interface PayrollRunDto {
  period: PeriodDto;
  run: { id: string | null; status: 'draft' | 'finalized'; version: number; finalizedAt: string | null; finalizedBy: string | null };
  source: 'live' | 'finalized';
  rates: PayrollRateDto[];
  /** Currency pairs some employee needs a month rate for. */
  neededRates: { fromCurrency: string; toCurrency: string }[];
  lines: PayrollLineDto[];
  withoutSalary: { employeeId: string; employeeName: string; employeeCode: string }[];
  totals: { currency: string; netPay: number; employees: number }[];
  rules: { overtimeMultiplier: number; hourlyRateHours: number; shortfall: 'percentage' | 'deduct' | 'none'; paidLeaveCounts: boolean; rounding: 'whole' | 'cents' };
  /** Why it can't be finalized yet (empty = can finalize). */
  blockers: string[];
}
